/**
 * Shared parse → classify → stage → import flow used by Finance and Daily Entry.
 *
 * Batch + drag-and-drop. Each dropped/picked file runs through the same
 * existing pipeline independently. Per-file outcomes are kept visible:
 *   parsing | ready | blocked | duplicate | staging | imported | failed
 *
 * A single confirmation code is entered once for the batch. Rejected,
 * duplicate, and failed rows never silently disappear.
 */
import React, { useState, useRef, useCallback } from 'react';
import { C } from '../../shared/design-tokens.js';
import type { FinanceDetectedType, ParsedFile } from '../../lib/finance-upload.js';
import {
  parseFinanceFile,
  commitFinanceFile,
} from '../../lib/finance-upload.js';
import { extractPdfTextClient } from '../../lib/pdf-client-extract.js';

type ItemStatus = 'parsing' | 'ready' | 'blocked' | 'duplicate' | 'committing' | 'imported' | 'failed';

interface FileItem {
  id: string;
  file: File;
  filename: string;
  size: number;
  status: ItemStatus;
  parsed?: ParsedFile;
  error?: string;
  importedRows?: number | null;
  // Client-extracted PDF text (pdfjs) when the worker's own extraction was
  // blocked by encryption. Sent on commit so the worker can populate
  // metadata + text_blocks.
  pdfExtractedText?: string;
}

interface FinanceUploadFlowProps {
  acceptedTypes: FinanceDetectedType[];
  rejectMessage: string;
  onImported?: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  gf_statement: 'GF report',
  service_worksheet: 'Weekly DSW',
  daily_service_worksheet: 'Daily DSW',
  settlement: 'Settlement',
  payroll_drivers: 'Driver payroll',
  payroll_managers: 'Manager payroll',
  payroll_marion: 'Marion payroll',
  ispa_attachment_c1: 'ISPA Attachment C-1 (rate contract)',
  pdf_document: 'PDF',
};

function formatType(t: FinanceDetectedType): string {
  if (!t) return 'Unknown';
  return TYPE_LABELS[t] || t;
}

function nextId(): string {
  return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
}

const STATUS_COLOR: Record<ItemStatus, string> = {
  parsing: C.gold,
  ready: C.green,
  blocked: C.red,
  duplicate: C.gold,
  committing: C.gold,
  imported: C.green,
  failed: C.red,
};

const STATUS_LABEL: Record<ItemStatus, string> = {
  parsing: 'Previewing…',
  ready: 'Ready (no writes yet)',
  blocked: 'Wrong module',
  duplicate: 'Already uploaded',
  committing: 'Importing…',
  imported: 'Imported',
  failed: 'Failed',
};

export default function FinanceUploadFlow({ acceptedTypes, rejectMessage, onImported }: FinanceUploadFlowProps) {
  const [items, setItems] = useState<FileItem[]>([]);
  const [code, setCode] = useState('');
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateItem = useCallback((id: string, patch: Partial<FileItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const acceptList = acceptedTypes;

  async function processFile(item: FileItem, file: File) {
    const result = await parseFinanceFile(file);
    if (!result.ok || !result.file) {
      updateItem(item.id, { status: 'failed', error: result.error || 'Preview failed.' });
      return;
    }
    const parsed = result.file;
    const detected = (parsed.detected_type || null) as FinanceDetectedType;

    // Server-side dedup indicator (read-only, no writes performed by /parse).
    // For dedup-hit PDFs whose existing substrate row is still
    // 'stored_uninterpreted' we still want to run client extraction so the
    // commit can upgrade the existing row in place.
    const existingStatus = parsed._dedup_hit?.ingestion_status;
    const needsClientPdfExtract =
      parsed._pdf && parsed._pdf.encrypted === true && (file.name.toLowerCase().endsWith('.pdf'));

    let clientText: string | undefined;
    if (needsClientPdfExtract) {
      const ex = await extractPdfTextClient(file);
      if (ex.ok && ex.text.length > 0) {
        clientText = ex.text;
      }
    }

    if (parsed._dedup_hit && parsed._dedup_hit.existing_source_id) {
      // If the prior row is stuck at stored_uninterpreted and we now have
      // client text, mark ready so the operator can re-commit to upgrade it.
      if (existingStatus === 'stored_uninterpreted' && clientText) {
        updateItem(item.id, { status: 'ready', parsed, pdfExtractedText: clientText });
      } else {
        updateItem(item.id, { status: 'duplicate', parsed, pdfExtractedText: clientText });
      }
      return;
    }

    if (!detected || !acceptList.includes(detected)) {
      updateItem(item.id, { status: 'blocked', parsed });
      return;
    }

    updateItem(item.id, { status: 'ready', parsed, pdfExtractedText: clientText });
  }

  function addFiles(files: File[]) {
    if (!files || files.length === 0) return;
    const newItems: FileItem[] = files.map((file) => ({
      id: nextId(),
      file: file,
      filename: file.name,
      size: file.size,
      status: 'parsing',
    }));
    setItems((prev) => [...prev, ...newItems]);
    setBatchError(null);
    newItems.forEach((item, idx) => {
      processFile(item, files[idx]);
    });
  }

  async function importReady() {
    const trimmedCode = code.trim();
    if (trimmedCode.length < 1) {
      setBatchError('Enter the confirmation code.');
      return;
    }
    setBatchError(null);
    setBatchRunning(true);
    let anyImported = false;

    // Sequential commits — re-uploads each file's bytes to /api/finance/commit
    // with the confirmation code. Permanent persistence happens here only.
    for (const item of items) {
      if (item.status !== 'ready' || !item.file) continue;

      updateItem(item.id, { status: 'committing', error: undefined });

      const committed = await commitFinanceFile(item.file, trimmedCode, item.pdfExtractedText);
      if (!committed.ok || !committed.result) {
        updateItem(item.id, { status: 'failed', error: committed.error || 'Commit failed.' });
        // Wrong confirmation code is a batch-stop — surface it once.
        if ((committed.error || '').toLowerCase().includes('confirmation')) {
          setBatchError(committed.error || 'Wrong confirmation code.');
          break;
        }
        continue;
      }
      const r = committed.result;
      updateItem(item.id, {
        status: 'imported',
        importedRows: typeof r.rows_created === 'number' ? r.rows_created : null,
        error: undefined,
      });
      anyImported = true;
    }

    setBatchRunning(false);
    if (anyImported && onImported) onImported();
  }

  function clearTerminal() {
    // Remove items whose status is terminal (imported / duplicate / blocked /
    // failed) so the operator can stage another batch without losing context
    // mid-run. `parsing`, `ready`, and `staging` rows are kept.
    setItems((prev) => prev.filter((it) => ['parsing', 'ready', 'staging'].includes(it.status)));
    setBatchError(null);
  }

  const readyCount = items.filter((it) => it.status === 'ready').length;
  const importedCount = items.filter((it) => it.status === 'imported').length;
  const failedCount = items.filter((it) => it.status === 'failed').length;
  const blockedCount = items.filter((it) => it.status === 'blocked').length;
  const duplicateCount = items.filter((it) => it.status === 'duplicate').length;
  const parsingCount = items.filter((it) => it.status === 'parsing').length;
  const committingCount = items.filter((it) => it.status === 'committing').length;
  const inFlight = parsingCount + committingCount > 0 || batchRunning;

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    if (!dragging) setDragging(true);
  }
  function onDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
  }
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const fileList = Array.from(e.dataTransfer.files || []) as File[];
    if (fileList.length > 0) addFiles(fileList);
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const fileList = Array.from(e.target.files || []) as File[];
    if (fileList.length > 0) addFiles(fileList);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <div>
      {/* Drop zone — always visible so operators can keep adding files */}
      <label
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          padding: items.length === 0 ? 28 : 18,
          border: `2px dashed ${dragging ? C.gold : C.borderSoft}`,
          borderRadius: 12,
          cursor: 'pointer',
          background: dragging ? 'rgba(201,162,39,0.08)' : 'rgba(255,255,255,0.02)',
          transition: 'background 120ms ease, border-color 120ms ease, padding 120ms ease',
        }}
      >
        <div style={{ fontSize: 14, color: C.text, textAlign: 'center' }}>
          {items.length === 0
            ? 'Drop files here, or click to choose'
            : 'Drop more files, or click to add'}
        </div>
        <div style={{ fontSize: 12, color: C.textMuted, textAlign: 'center' }}>
          Accepted: {acceptedTypes.map((t) => formatType(t)).join(', ')} · multiple files OK
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv,.pdf"
          multiple
          onChange={onPick}
          style={{ display: 'none' }}
        />
      </label>

      {/* Per-file rows */}
      {items.length > 0 ? (
        <div style={{ marginTop: 14, display: 'grid', gap: 8 }}>
          {items.map((it) => (
            <FileRow
              key={it.id}
              item={it}
              rejectMessage={rejectMessage}
            />
          ))}
        </div>
      ) : null}

      {/* Batch summary + actions */}
      {items.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: C.textMuted, marginBottom: 12 }}>
            <span>{items.length} file{items.length === 1 ? '' : 's'}</span>
            {readyCount > 0 ? <span style={{ color: C.green }}>{readyCount} ready</span> : null}
            {parsingCount > 0 ? <span style={{ color: C.gold }}>{parsingCount} parsing</span> : null}
            {importedCount > 0 ? <span style={{ color: C.green }}>{importedCount} imported</span> : null}
            {duplicateCount > 0 ? <span style={{ color: C.gold }}>{duplicateCount} duplicate</span> : null}
            {blockedCount > 0 ? <span style={{ color: C.red }}>{blockedCount} blocked</span> : null}
            {failedCount > 0 ? <span style={{ color: C.red }}>{failedCount} failed</span> : null}
          </div>

          {readyCount > 0 ? (
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <label className="field-label" htmlFor="fin-upload-code">Confirmation code</label>
                <input
                  id="fin-upload-code"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter code to import"
                  className="field-input"
                  disabled={batchRunning}
                />
              </div>
              {batchError ? (
                <div style={{ color: C.red, fontSize: 13 }}>{batchError}</div>
              ) : null}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={importReady}
                  className="btn btn-primary"
                  disabled={batchRunning || code.trim().length < 1 || readyCount === 0 || inFlight}
                >
                  {batchRunning ? 'Importing…' : `Import ${readyCount} ready file${readyCount === 1 ? '' : 's'}`}
                </button>
                {(importedCount + duplicateCount + blockedCount + failedCount) > 0 ? (
                  <button
                    type="button"
                    onClick={clearTerminal}
                    className="btn btn-ghost"
                    disabled={batchRunning}
                  >
                    Clear finished rows
                  </button>
                ) : null}
              </div>
            </div>
          ) : (importedCount + duplicateCount + blockedCount + failedCount) > 0 && parsingCount === 0 ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={clearTerminal}
                className="btn btn-secondary"
              >
                Clear finished rows
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function FileRow({ item, rejectMessage }: { item: FileItem; rejectMessage: string }) {
  const dt = (item.parsed?.detected_type || null) as FinanceDetectedType;
  const trace = item.parsed?._detect_trace;
  const rows = item.parsed?.raw_row_count ?? item.parsed?.raw_rows?.length;
  const week = item.parsed?.week_ending;
  const sizeKb = (item.size / 1024).toFixed(1);
  const color = STATUS_COLOR[item.status];
  const pdfMeta = item.parsed?._pdf;
  const ispa = item.parsed?._ispa_metadata;

  return (
    <div
      style={{
        padding: 12,
        borderRadius: 10,
        background: 'rgba(255,255,255,0.04)',
        border: `1px solid ${C.borderSoft}`,
        display: 'grid',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 220px' }}>
          <div style={{ color: C.text, fontSize: 13, fontWeight: 600, wordBreak: 'break-word' }}>{item.filename}</div>
          <div style={{ color: C.textMuted, fontSize: 11, marginTop: 2 }}>{sizeKb} KB</div>
        </div>
        <div
          style={{
            fontSize: 11,
            padding: '3px 10px',
            borderRadius: 999,
            color: color,
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${color}`,
            whiteSpace: 'nowrap',
          }}
        >
          {STATUS_LABEL[item.status]}
          {item.status === 'imported' && typeof item.importedRows === 'number'
            ? ` · ${item.importedRows} row${item.importedRows === 1 ? '' : 's'}`
            : null}
        </div>
      </div>

      {item.parsed ? (
        <div style={{ display: 'grid', gap: 3, fontSize: 12, color: C.textMuted }}>
          <div>
            Detected: <span style={{ color: C.gold }}>{formatType(dt)}</span>
            {trace ? <span style={{ marginLeft: 8, fontFamily: 'DM Mono, monospace', fontSize: 11 }}>({trace})</span> : null}
          </div>
          {(rows != null) || week ? (
            <div>
              {rows != null ? <span>Rows: <span style={{ color: C.text }}>{rows}</span></span> : null}
              {rows != null && week ? <span>  ·  </span> : null}
              {week ? <span>Week ending: <span style={{ color: C.text }}>{String(week)}</span></span> : null}
            </div>
          ) : null}
          {ispa ? (
            <div style={{ display: 'grid', gap: 2 }}>
              {ispa.ispa_number ? <div>ISPA No: <span style={{ color: C.text }}>{ispa.ispa_number}</span></div> : null}
              {ispa.effective_date || ispa.end_date ? (
                <div>
                  {ispa.effective_date ? <span>Effective: <span style={{ color: C.text }}>{ispa.effective_date}</span></span> : null}
                  {ispa.effective_date && ispa.end_date ? <span>  ·  </span> : null}
                  {ispa.end_date ? <span>Through: <span style={{ color: C.text }}>{ispa.end_date}</span></span> : null}
                </div>
              ) : null}
              {ispa.signers && ispa.signers.length > 0 ? (
                <div>Signed by: <span style={{ color: C.text }}>{ispa.signers.map((s) => `${s.name} (${s.party})`).join(', ')}</span></div>
              ) : null}
            </div>
          ) : null}
          {pdfMeta ? (
            <div style={{ fontSize: 11 }}>
              {pdfMeta.encrypted
                ? <span>PDF encrypted \u2014 raw preserved, text extraction deferred.</span>
                : (pdfMeta.extraction_ok && pdfMeta.text_length > 0
                    ? <span>PDF text extracted ({pdfMeta.text_length.toLocaleString()} chars).</span>
                    : <span>PDF \u2014 raw preserved; no text extracted yet.</span>)
              }
            </div>
          ) : null}
        </div>
      ) : null}

      {item.status === 'blocked' ? (
        <div style={{ marginTop: 4, fontSize: 12, color: C.red, lineHeight: 1.5 }}>
          {rejectMessage}
        </div>
      ) : null}

      {item.error ? (
        <div style={{ marginTop: 4, fontSize: 12, color: C.red, lineHeight: 1.5 }}>
          {item.error}
        </div>
      ) : null}
    </div>
  );
}
